import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatProductPrice, getMarketProductId, getPaymentProduct, MARKET_GLOBAL, MARKET_KR } from '../../payments/catalog';
import { initPayment, loadTossPaymentsScript } from '../../payments/client';
import { capturePaypalOrder, createPaypalOrder, loadPaypalSdk } from '../../payments/paypalClient';
import { useMarket } from '../../payments/useMarket';
import { isLoggedIn } from '../../auth/auth';
import { sanitizeReturnTo } from '../../auth/returnTo';
import { getPaymentAnalyticsContext, trackEvent, trackEventOnce, trackConfirmedPurchase } from '../../utils/analytics';
import { useTranslation } from 'react-i18next';
import { localize } from '../../i18n/locale';
import DomesticPaymentOptions from './DomesticPaymentOptions.jsx';

const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#fff',
    padding: '2rem',
    textAlign: 'center',
};

export default function PaymentStart() {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage || i18n.language;
    const tr = useCallback((ko, en) => localize(language, { ko, en }), [language]);
    const { productId } = useParams();
    const [searchParams] = useSearchParams();
    const { loading: marketLoading, data: marketData } = useMarket();
    const paypalContainerRef = useRef(null);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [domesticProvider, setDomesticProvider] = useState(
        searchParams.get('provider') === 'toss' ? 'toss' : null
    );

    const returnTo = sanitizeReturnTo(searchParams.get('returnTo') || '', '');
    const resultId = String(searchParams.get('resultId') || '').trim();
    const guestPurchase = Boolean(resultId && !isLoggedIn());
    const market = marketData.market;
    const marketProductId = getMarketProductId(productId, market);
    const product = getPaymentProduct(marketProductId || productId);
    const isGlobal = market === MARKET_GLOBAL;

    useEffect(() => {
        if (marketLoading) return;

        if (!marketProductId) {
            trackEvent('payment_failed', {
                product_id: productId,
                market,
                payment_provider: marketData.payment_provider,
                failure_reason: 'provider_error',
            });
            navigate('/payments/fail?message=Unsupported%20product&analytics_tracked=1', { replace: true });
            return;
        }

        if (marketProductId !== productId) {
            const nextParams = new URLSearchParams();
            if (returnTo) nextParams.set('returnTo', returnTo);
            if (resultId) nextParams.set('resultId', resultId);
            const query = nextParams.size ? `?${nextParams.toString()}` : '';
            navigate(`/payments/start/${marketProductId}${query}`, { replace: true });
        }
    }, [marketData.payment_provider, marketLoading, marketProductId, market, navigate, productId, resultId, returnTo]);

    useEffect(() => {
        if (marketLoading || !marketProductId || marketProductId !== productId || market !== MARKET_KR || domesticProvider !== 'toss') {
            return undefined;
        }

        let cancelled = false;

        async function startToss() {
            const tossProduct = getPaymentProduct(productId);
            const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY;
            const isValidApiClientKey = /^(test|live)_ck_/.test(clientKey || '');
            if (!isValidApiClientKey) {
                trackEvent('payment_failed', {
                    product_id: productId,
                    currency: 'KRW',
                    value: tossProduct?.amount,
                    payment_provider: 'toss',
                    failure_reason: 'provider_error',
                });
                const message = clientKey?.includes('_gck_')
                    ? tr('결제위젯 키가 아닌 API 개별 연동 클라이언트 키가 필요합니다.', 'An API integration client key is required instead of a payment-widget key.')
                    : tr('토스 API 개별 연동 클라이언트 키가 설정되지 않았습니다.', 'The Toss API integration client key is not configured.');
                setError(message);
                setDomesticProvider(null);
                return;
            }

            try {
                const [TossPayments, initData] = await Promise.all([
                    loadTossPaymentsScript(),
                    initPayment(productId, returnTo),
                ]);
                if (cancelled) return;

                const tossPayments = TossPayments(clientKey);
                const payment = tossPayments.payment({
                    customerKey: initData.customer_key,
                });

                trackEvent('begin_checkout', {
                    currency: 'KRW',
                    value: initData.amount || tossProduct?.amount,
                    payment_provider: 'toss',
                    product_id: productId,
                    package_type: tossProduct?.creditCount ? 'chemistry_pass' : 'premium_report',
                    market,
                    source_page: 'payment_start',
                    ...getPaymentAnalyticsContext(productId),
                });

                trackEventOnce('payment_start', 'toss:' + initData.order_id, {
                    currency: 'KRW', value: initData.amount, payment_provider: 'toss', market,
                    source_page: 'payment_start', ...getPaymentAnalyticsContext(productId),
                });

                await payment.requestPayment({
                    method: 'CARD',
                    amount: {
                        value: initData.amount,
                        currency: 'KRW',
                    },
                    orderId: initData.order_id,
                    orderName: initData.order_name,
                    successUrl: initData.success_url,
                    failUrl: initData.fail_url,
                });
            } catch (err) {
                if (cancelled) return;
                trackEvent('payment_failed', {
                    product_id: productId,
                    market,
                    payment_provider: 'toss',
                    currency: 'KRW',
                    value: tossProduct?.amount,
                    failure_reason: Number(err?.status) === 0 ? 'network_error' : 'provider_error',
                });
                navigate(`/payments/fail?message=${encodeURIComponent(err.message || 'Payment start failed')}&analytics_tracked=1`, { replace: true });
            }
        }

        startToss();
        return () => { cancelled = true; };
    }, [domesticProvider, marketLoading, marketProductId, market, navigate, productId, returnTo, tr]);

    useEffect(() => {
        if (marketLoading || !marketProductId || marketProductId !== productId || !isGlobal || !paypalContainerRef.current) {
            return undefined;
        }

        let cancelled = false;
        const container = paypalContainerRef.current;
        container.innerHTML = '';
        setStatus(tr('PayPal 결제를 준비하고 있습니다.', 'Preparing PayPal checkout.'));

        loadPaypalSdk()
            .then((paypal) => {
                if (cancelled || !paypal?.Buttons) return;
                paypal.Buttons({
                    style: {
                        layout: 'vertical',
                        shape: 'rect',
                        label: 'paypal',
                    },
                    createOrder: async () => {
                        const order = await createPaypalOrder(productId, resultId);
                        trackEvent('begin_checkout', {
                            currency: 'USD',
                            value: product?.amount,
                            payment_provider: 'paypal',
                            product_id: productId,
                            package_type: 'chemistry_pass',
                            market,
                            source_page: 'payment_start',
                        });
                        trackEventOnce('payment_start', 'paypal:' + order.order_id, {
                            currency: 'USD', value: product?.amount, payment_provider: 'paypal', market,
                            source_page: 'payment_start', ...getPaymentAnalyticsContext(productId),
                        });
                        return order.order_id;
                    },
                    onApprove: async (data) => {
                        try {
                            setStatus(tr('결제를 확인하고 있습니다.', 'Verifying your payment.'));
                            const result = await capturePaypalOrder(data.orderID, productId);
                            if (result.status !== 'confirmed') throw new Error('payment_not_confirmed');
                            window.dispatchEvent(new Event('premium-entitlements-updated'));
                            trackConfirmedPurchase(result, {
                                currency: 'USD',
                                value: product?.amount,
                                payment_provider: 'paypal',
                                market,
                                product_id: productId,
                                ...getPaymentAnalyticsContext(productId),
                                items: [{ item_id: productId, item_name: productId, price: product?.amount, quantity: 1 }],
                            });
                            navigate(returnTo || product?.route || '/mypage', { replace: true });
                        } catch (error) {
                            console.error('PayPal verification failed:', error);
                            trackEvent('payment_failed', {
                                payment_provider: 'paypal',
                                product_id: productId,
                                currency: 'USD',
                                value: product?.amount,
                                failure_reason: 'verification_failed', failure_stage: 'confirmation', error_type: 'verification_failed',
                            ...getPaymentAnalyticsContext(productId),
                            });
                            setError(tr('PayPal 결제를 확인하지 못했습니다.', 'Could not verify the PayPal payment.'));
                        }
                    },
                    onCancel: () => {
                        trackEvent('payment_failed', {
                            payment_provider: 'paypal',
                            product_id: productId,
                            currency: 'USD',
                            value: product?.amount,
                            failure_reason: 'user_cancelled', failure_stage: 'cancellation', error_type: 'user_cancelled',
                            ...getPaymentAnalyticsContext(productId),
                        });
                        setStatus(tr('결제가 취소되었습니다.', 'Payment was cancelled.'));
                    },
                    onError: (err) => {
                        console.error('PayPal Checkout failed:', err);
                        trackEvent('payment_failed', {
                            payment_provider: 'paypal',
                            product_id: productId,
                            currency: 'USD',
                            value: product?.amount,
                            failure_reason: 'provider_error', failure_stage: 'provider_open', error_type: 'provider_error',
                            ...getPaymentAnalyticsContext(productId),
                        });
                        setError(err?.message || tr('PayPal 결제를 시작하지 못했습니다.', 'Could not start PayPal checkout.'));
                    },
                }).render(container);
                setStatus('');
            })
            .catch((err) => {
                console.error(err);
                trackEvent('payment_failed', {
                    payment_provider: 'paypal',
                    product_id: productId,
                    currency: 'USD',
                    value: product?.amount,
                    failure_reason: 'provider_error',
                });
                setError(err?.message || tr('PayPal 결제를 시작하지 못했습니다.', 'Could not start PayPal checkout.'));
            });

        return () => {
            cancelled = true;
            container.innerHTML = '';
        };
    }, [isGlobal, marketLoading, marketProductId, market, navigate, product, productId, resultId, returnTo, tr]);

    return (
        <div style={containerStyle}>
            <div style={{ width: 'min(420px, 100%)' }}>
                <h1 style={{ marginBottom: '1rem' }}>{tr('결제를 준비하고 있습니다...', 'Preparing payment...')}</h1>
                <p style={{ color: '#cbd5e1' }}>
                    {error || status || (isGlobal
                        ? tr('PayPal로 결제할 상품을 확인하고 있습니다.', 'Confirm your PayPal checkout below.')
                        : tr('결제수단을 선택해 주세요.', 'Choose a payment method.'))}
                </p>
                {!isGlobal && product ? (
                    <DomesticPaymentOptions
                        language={language}
                        market={market}
                        product={product}
                        productId={productId}
                        resultId={resultId}
                        guestPurchase={guestPurchase}
                        returnTo={returnTo}
                        tossProcessing={domesticProvider === 'toss'}
                        onToss={() => { setError(''); setStatus(''); setDomesticProvider('toss'); }}
                    />
                ) : null}
                {isGlobal && product ? (
                    <div style={{ marginTop: '1.5rem' }}>
                        <p style={{ color: '#f8fafc', fontWeight: 700 }}>{product.title_en || product.title}</p>
                        <p style={{ color: '#cbd5e1' }}>{formatProductPrice(product, language)}</p>
                        <div ref={paypalContainerRef} style={{ marginTop: '1rem' }} />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
